// src/teacherTermsContent.js
//
// THE single canonical source of the Teacher Partner Terms body text.
//
// Consumed by BOTH surfaces so the wording only ever lives in one place:
//   1. The in-app acceptance screen  — src/TeacherTermsView.js imports this module directly.
//   2. The public gated page         — scripts/build-teacher-terms.js reads this module (same
//      string-extraction trick as scripts/build-affiliate-strings.js) and regenerates the
//      section markup inside functions/affiliate/teacher-terms.html between its
//      <!-- TEACHER_TERMS:*:START --> / :END markers. That script is wired into the `functions`
//      predeploy in firebase.json, so a deploy can never ship a stale copy.
//
// Only the acceptance control differs between the two surfaces (a real checkbox + Accept button
// in the app; a "log in to accept" button on the public page) — everything down to and including
// the "Required Checkbox" sub-heading is shared and comes from here.
//
// The terms are ENGLISH ONLY by decision — do not translate this file. (The surrounding app
// chrome — banner, screen title, dashboard status line — IS translated, in src/i18n/*.)
//
// ── Block shapes ──
//   { p:  <inline> }                      paragraph
//   { ul: [<inline>, …] }                 bullet list
//   { ol: [<inline>, …] }                 numbered list
//   { h3: "…" }                           sub-heading inside a section
//   { details: { summary, blocks:[…] } }  native <details> callout (illustrative only)
// where <inline> is a string, or an array mixing strings and { b: "bold run" } objects.

// Internal identifier for this revision — stored on every acceptance record and shown as
// "Version {version}" on the Teacher Dashboard. Bump it (a new date) whenever the wording below
// changes materially.
export const TEACHER_TERMS_VERSION = "2026-09-10";

// Shown on BOTH surfaces. There is NO fixed "effective date" — for each teacher the Terms take
// effect on the date they accept them in-app (TeacherTermsView shows that date; the acceptance
// record's acceptedAt is the authoritative timestamp). So only "last updated" lives here.
export const TEACHER_TERMS_LAST_UPDATED = "September 2026";

// SHA-256 of the canonical contract text — DO NOT edit by hand. Regenerated in place by
// scripts/build-teacher-terms.js (which also writes functions/teacherTermsMeta.json) so the
// client and the acceptance backend pin the exact same wording.
export const TEACHER_TERMS_HASH = "13a6dab88947df046a764c5078d9b561f289d828b4347f292b119cf4ebf50805"; // build-teacher-terms:hash

// Set true only if the version is reset to the "DRAFT-PENDING-DATE" sentinel — then the whole
// acceptance flow (banner, dashboard line, callable) is disabled. Normally false.
export const TEACHER_TERMS_DRAFT = TEACHER_TERMS_VERSION === "DRAFT-PENDING-DATE";

export const TEACHER_TERMS_INTRO =
  'These Teacher Partner Terms ("Terms") govern your participation in the Speafy Teacher ' +
  'Partner Program ("Program"). By accepting these Terms in your Speafy account, you agree to ' +
  'be legally bound by them.';

export const TEACHER_TERMS_ACCEPTANCE = {
  requiredHeading: "Required Checkbox",
  // Contract text — shown verbatim next to the checkbox in the app. English only.
  checkboxLabel:
    "I have read, understood, and agree to the Speafy Teacher Partner Terms. I confirm that the " +
    "information I have provided is accurate and agree to be bound by these Terms, including the " +
    "referral and commission rules, payout conditions, data-protection obligations, and " +
    "termination provisions.",
  buttonLabel: "Accept & Join the Teacher Partner Program",
};

export const TEACHER_TERMS_SECTIONS = [
  {
    n: 1,
    title: "About the Program",
    blocks: [
      { p: "The Speafy Teacher Partner Program allows eligible language teachers to recommend Speafy to their students as a complementary language-learning tool." },
      { p: "Speafy is an AI-powered language-learning service. The Program is a referral and partnership program only. Your participation does not make you an employee, agent, representative, franchisee, or legal representative of Speafy." },
      { p: "You remain independent and responsible for your own teaching activities, students, professional obligations, taxes, and legal compliance." },
    ],
  },
  {
    n: 2,
    title: "Eligibility",
    blocks: [
      { p: "To participate, you must:" },
      { ul: [
        "provide accurate and complete registration and payout information;",
        "be legally permitted to participate in a referral or partner program in your country;",
        "use Speafy only in accordance with these Terms and applicable law; and",
        "maintain accurate and up-to-date contact and payout information.",
      ] },
      { p: "You may participate only on your own behalf unless Speafy expressly agrees otherwise in writing." },
      { p: "Speafy may decline, suspend, or terminate participation where it reasonably determines that eligibility requirements are not met or that participation creates legal, financial, security, fraud, or compliance concerns." },
    ],
  },
  {
    n: 3,
    title: "How the Referral Program Works",
    blocks: [
      { p: 'Speafy may provide you with a unique referral link, code, or other attribution method ("Referral Link").' },
      { p: "You may share your Referral Link with your own students and explain Speafy to them in an accurate and non-misleading manner." },
      { p: "A student will generally be considered your referred student only when:" },
      { ol: [
        "the student signs up using your Referral Link or otherwise becomes correctly attributed to you through Speafy's referral system; and",
        "Speafy records the student as associated with your partner account.",
      ] },
      { p: "Speafy's records regarding referral attribution, subscription payments, refunds, cancellations, and commission calculations will be used to determine commissions, except in the case of a demonstrable technical or administrative error." },
      { p: "A student cannot be attributed to more than one teacher partner for the same referral relationship. Speafy may establish reasonable rules for resolving duplicate, conflicting, or disputed referrals." },
    ],
  },
  {
    n: 4,
    title: "Your Relationship with Your Students",
    blocks: [
      { p: "Your relationship with your students remains separate from Speafy." },
      { p: "You are responsible for your teaching services, fees, schedules, communications, and other arrangements with your students." },
      { p: "You must not represent that:" },
      { ul: [
        "Speafy is your employer;",
        "you are an employee, agent, or authorized representative of Speafy;",
        "Speafy guarantees or endorses your teaching services;",
        "you have authority to make commitments on behalf of Speafy; or",
        "Speafy guarantees any particular educational result.",
      ] },
      { p: "You must not make medical, legal, financial, or other professional claims about Speafy that are not supported by information officially provided by Speafy." },
    ],
  },
  {
    n: 5,
    title: "Commission",
    blocks: [
      { p: [
        "For each eligible referred student, you earn a commission equal to ",
        { b: "20% of the amount actually paid by that student to Speafy" },
        " for an eligible subscription billing period.",
      ] },
      { p: "The commission is calculated based on the amount actually received by Speafy, not merely the advertised subscription price." },
      { p: "No commission is earned on an amount that the student does not actually pay." },
      { p: "Unless expressly stated otherwise by Speafy, commissions do not apply to:" },
      { ul: [
        "taxes or government charges collected from the student;",
        "amounts refunded to the student;",
        "reversed or charged-back transactions;",
        "fraudulent transactions;",
        "payments that Speafy does not actually receive; or",
        "transactions that do not qualify under these Terms.",
      ] },
      { p: "Speafy may reasonably adjust its commission calculation where necessary to account for refunds, chargebacks, payment reversals, fraud, duplicate transactions, technical errors, or other circumstances affecting the amount actually received from the student." },
    ],
  },
  {
    n: 6,
    title: "Twelve-Month Commission Period",
    blocks: [
      { p: [
        "For each referred student, commission is available only for payments made during the student's ",
        { b: "first 12 months following the date of the student's signup" },
        ".",
      ] },
      { p: "The 12-month period is calculated separately for each referred student." },
      { p: "After that student's 12-month period ends, no further commission is earned from that student, even if the student remains subscribed to Speafy." },
      { details: {
        summary: "Example — Monthly Subscription",
        blocks: [
          { p: "If a student signs up on 1 March 2026 and makes eligible monthly payments, you earn 20% of those eligible payments made during the first 12 months following signup. Payments made after the applicable 12-month period do not generate commission." },
        ],
      } },
      { details: {
        summary: "Example — Annual Subscription",
        blocks: [
          { p: "If a student signs up on 1 March 2026 and pays for an annual subscription in one payment, that payment may generate one commission because it is made within the student's applicable 12-month period. A renewal payment made after that period does not generate further commission." },
        ],
      } },
    ],
  },
  {
    n: 7,
    title: "No Commission Without Student Payment",
    blocks: [
      { p: "If a referred student:" },
      { ul: [
        "does not pay;",
        "pauses their subscription without making a payment;",
        "cancels their subscription;",
        "receives a full or partial refund;",
        "has a payment reversed or charged back; or",
        "otherwise does not generate an amount actually received by Speafy,",
      ] },
      { p: "you do not earn commission on the corresponding amount." },
    ],
  },
  {
    n: 8,
    title: "Commission Balance and Payout Threshold",
    blocks: [
      { p: "Commissions from all eligible referred students are accumulated in your partner balance." },
      { p: [
        "Speafy will initiate a payout when your ",
        { b: "unpaid commission balance reaches US$50" },
        ", subject to these Terms and any applicable payment-processing requirements.",
      ] },
      { p: "There is no guaranteed monthly or fixed payout date." },
      { p: "If your unpaid balance is below US$50, it carries forward until it reaches the applicable payout threshold." },
      { p: "Speafy may withhold or delay a payout where reasonably necessary to verify a transaction, investigate suspected fraud or abuse, resolve a dispute, comply with law, or address a payment-processing issue." },
    ],
  },
  {
    n: 9,
    title: "Payout Methods",
    blocks: [
      { p: ["Payouts will normally be made through ", { b: "Wise" }, "."] },
      { p: [
        "If Wise cannot reasonably be used for your country, account, or circumstances, Speafy may use ",
        { b: "Payoneer" },
        " or another payment method that Speafy makes available.",
      ] },
      { p: "You are responsible for providing complete, accurate, and current payout information." },
      { p: "Speafy is not responsible for delays, failed transfers, or losses resulting from incorrect, incomplete, outdated, or restricted payout information provided by you." },
    ],
  },
  {
    n: 10,
    title: "Transfer and Payment Fees",
    blocks: [
      { p: "Any transfer, receiving, conversion, intermediary-bank, or payment-processing fees charged in connection with receiving your payout may be deducted from the amount paid to you." },
      { p: "The amount you ultimately receive may therefore be less than your accrued commission balance." },
      { p: "You are responsible for any fees charged by your own bank, payment provider, or other intermediary." },
    ],
  },
  {
    n: 11,
    title: "Corrections and Adjustments",
    blocks: [
      { p: "Speafy may correct or adjust your commission balance where necessary to reflect the actual amount received from a referred student." },
      { p: "Examples include:" },
      { ul: [
        "a student receiving a refund;",
        "a payment being reversed or charged back;",
        "fraudulent or unauthorized activity;",
        "duplicate commission;",
        "an accounting or technical error; or",
        "incorrect attribution.",
      ] },
      { p: "Any correction will be made for the purpose of maintaining an accurate commission balance." },
      { p: "If an adjustment results in a negative balance, Speafy may offset that amount against future commissions, where legally permitted." },
    ],
  },
  {
    n: 12,
    title: "Taxes",
    blocks: [
      { p: "You are solely responsible for determining and complying with your tax obligations arising from commissions you receive through the Program, including any income tax, VAT/GST, withholding, reporting, registration, or other obligations applicable to you." },
      { p: "Speafy may request tax or other information reasonably necessary to make payments or comply with applicable law." },
      { p: "Speafy may withhold amounts where required by applicable law." },
      { p: "Participation in the Program does not create an employment relationship between you and Speafy." },
    ],
  },
  {
    n: 13,
    title: "Your Marketing Activities",
    blocks: [
      { p: "You may promote Speafy only using truthful, accurate, and lawful information." },
      { p: "You must not:" },
      { ul: [
        "make false, misleading, deceptive, or unsubstantiated claims about Speafy;",
        "promise specific learning outcomes that Speafy does not guarantee;",
        "alter Speafy branding in a way that creates confusion about the relationship;",
        "impersonate Speafy;",
        "create websites, social-media accounts, advertisements, or other materials that falsely appear to be operated by Speafy;",
        "engage in spam or unsolicited bulk communications;",
        "use misleading advertising;",
        "purchase search-engine keywords or advertisements that intentionally impersonate Speafy without prior authorization;",
        "use malware, bots, automated systems, cookie stuffing, forced clicks, or other artificial methods to generate referrals;",
        "create self-referrals or referrals designed primarily to obtain commissions improperly;",
        "encourage a student to create multiple accounts to generate additional commissions; or",
        "engage in any activity intended to manipulate the referral or commission system.",
      ] },
      { p: "Speafy may reject commissions generated through prohibited or abusive activities." },
    ],
  },
  {
    n: 14,
    title: "Student Choice and No Pressure",
    blocks: [
      { p: "Students must be free to decide whether to use Speafy." },
      { p: "You must not make Speafy use a condition of receiving your independent teaching services unless that arrangement is lawful and transparently disclosed to the student." },
      { p: "You must not pressure, deceive, or improperly incentivize a student to subscribe solely for the purpose of generating your commission." },
    ],
  },
  {
    n: 15,
    title: "Student Information and Privacy",
    blocks: [
      { p: "You must handle student personal information in accordance with applicable privacy and data-protection laws." },
      { p: "You must not obtain, collect, download, copy, sell, disclose, or otherwise use Speafy student information except as permitted by applicable law and expressly authorized by Speafy." },
      { p: "If Speafy provides you with student progress information or reports, you may use that information only for the educational purpose for which it is provided and in accordance with Speafy's applicable privacy requirements." },
      { p: "You must take reasonable measures to protect any student information you receive from unauthorized access, disclosure, alteration, or loss." },
      { p: "You must promptly notify Speafy if you become aware of unauthorized access to or disclosure of Speafy-related student information." },
      { p: "Nothing in these Terms gives you ownership of Speafy's databases, student lists, or personal data." },
    ],
  },
  {
    n: 16,
    title: "Confidentiality",
    blocks: [
      { p: "You must keep confidential any non-public information that Speafy provides to you in connection with the Program." },
      { p: "Confidential information may include non-public business information, product information, pricing information, partner information, technical information, student information, and commission information." },
      { p: "You may use confidential information only as necessary to participate in the Program." },
      { p: "This obligation does not apply to information that:" },
      { ul: [
        "is publicly available through no breach of these Terms;",
        "was lawfully known to you before Speafy disclosed it;",
        "is independently developed without using confidential information; or",
        "must be disclosed by law, provided that you give Speafy reasonable notice where legally permitted.",
      ] },
    ],
  },
  {
    n: 17,
    title: "Intellectual Property",
    blocks: [
      { p: "Speafy and its licensors retain all rights, title, and interest in Speafy, including its software, website, trademarks, logos, content, technology, documentation, and other intellectual property." },
      { p: "Subject to these Terms, Speafy grants you a limited, non-exclusive, non-transferable, revocable permission to use Speafy-provided referral materials and branding solely to promote Speafy through the Program." },
      { p: "You must stop using Speafy branding and promotional materials when your participation ends or when Speafy requests that you do so." },
      { p: "You must not:" },
      { ul: [
        "register Speafy or confusingly similar trademarks, domains, or social-media names;",
        "copy or modify Speafy software or proprietary materials;",
        "claim ownership of Speafy intellectual property; or",
        "use Speafy intellectual property outside the purposes permitted by these Terms.",
      ] },
    ],
  },
  {
    n: 18,
    title: "Content You Provide",
    blocks: [
      { p: "If you voluntarily provide Speafy with testimonials, feedback, suggestions, reviews, photographs, videos, or other materials for use in connection with the Program, you represent that you have the necessary rights to provide them." },
      { p: "You grant Speafy a non-exclusive, worldwide, royalty-free license to use such materials for operating, improving, or promoting the Program, subject to applicable privacy and consent requirements." },
      { p: "You are not required to provide promotional content unless otherwise expressly agreed." },
    ],
  },
  {
    n: 19,
    title: "Independent Contractor Relationship",
    blocks: [
      { p: "You participate in the Program as an independent partner." },
      { p: "Nothing in these Terms creates:" },
      { ul: [
        "an employment relationship;",
        "a partnership;",
        "a joint venture;",
        "an agency relationship;",
        "a franchise relationship; or",
        "fiduciary duties between you and Speafy,",
      ] },
      { p: "except to the extent a non-waivable obligation is imposed by applicable law." },
      { p: "You have no authority to bind Speafy, enter into agreements on Speafy's behalf, incur obligations for Speafy, or make representations as an authorized representative of Speafy." },
    ],
  },
  {
    n: 20,
    title: "Your Representations and Obligations",
    blocks: [
      { p: "By participating, you represent that:" },
      { ul: [
        "the information you provide to Speafy is accurate;",
        "you are legally capable of entering into these Terms;",
        "your participation complies with applicable law;",
        "your promotional activities comply with applicable advertising and privacy requirements;",
        "you have all necessary rights and permissions for materials you provide to Speafy; and",
        "you will comply with these Terms and reasonable Program instructions communicated by Speafy.",
      ] },
    ],
  },
  {
    n: 21,
    title: "Suspension and Termination",
    blocks: [
      { p: "You may stop participating in the Program at any time by notifying Speafy or following any available account-closure procedure." },
      { p: "Speafy may suspend or terminate your participation if:" },
      { ul: [
        "you breach these Terms;",
        "you engage in fraudulent, abusive, misleading, or prohibited activity;",
        "your participation creates a legal, regulatory, security, or reputational risk;",
        "your account or referral activity appears compromised;",
        "required information is not provided;",
        "the Program is discontinued; or",
        "Speafy otherwise has a legitimate reason to discontinue the relationship, subject to applicable law.",
      ] },
      { p: "Where appropriate, Speafy may provide notice and an opportunity to address a breach before termination." },
    ],
  },
  {
    n: 22,
    title: "Effect of Termination",
    blocks: [
      { p: "When your participation ends:" },
      { ul: [
        "you must stop representing yourself as a Speafy Teacher Partner;",
        "you must stop using Speafy referral links and promotional materials;",
        "no new students will normally be attributed to you after termination; and",
        "commissions properly earned before termination remain subject to these Terms.",
      ] },
      { p: "Speafy may delay payment of commissions where reasonably necessary to account for refunds, chargebacks, fraud investigations, payment reversals, or other post-termination adjustments." },
      { p: "Unless required by applicable law or expressly agreed otherwise, termination does not create a right to future commissions from payments made after termination." },
    ],
  },
  {
    n: 23,
    title: "Changes to the Program or These Terms",
    blocks: [
      { p: "Speafy may update the Program or these Terms from time to time." },
      { p: "If a change materially affects your rights or obligations, Speafy will provide reasonable notice through the website, your partner account, email, or another appropriate communication method." },
      { p: "Your continued participation after the effective date of updated Terms constitutes acceptance of the updated Terms where such acceptance is legally valid." },
      { p: "If you do not agree to a material change, you may stop participating in the Program before the change becomes effective." },
      { p: "Changes will not retroactively eliminate commissions that were already validly earned, except where an adjustment is necessary to correct a refund, chargeback, fraud, payment reversal, or error." },
    ],
  },
  {
    n: 24,
    title: "Disclaimers",
    blocks: [
      { p: "The Program is provided subject to availability." },
      { p: "Speafy does not guarantee:" },
      { ul: [
        "any particular number of referred students;",
        "any minimum amount of commissions;",
        "continued availability of the Program;",
        "uninterrupted operation of referral tracking; or",
        "any particular educational outcome from use of Speafy.",
      ] },
      { p: "Nothing in these Terms limits any rights or remedies that cannot lawfully be limited or excluded." },
    ],
  },
  {
    n: 25,
    title: "Limitation of Liability",
    blocks: [
      { p: "To the maximum extent permitted by applicable law, Speafy will not be liable for indirect, incidental, special, consequential, or punitive losses arising from your participation in the Program, including loss of anticipated commissions or business opportunities." },
      { p: "Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited." },
    ],
  },
  {
    n: 26,
    title: "Indemnification",
    blocks: [
      { p: "To the extent permitted by applicable law, you are responsible for losses, claims, liabilities, costs, or expenses arising from your breach of these Terms, unlawful conduct, infringement of third-party rights, or unauthorized representations concerning Speafy." },
      { p: "This provision does not apply to the extent the relevant loss was caused by Speafy's own unlawful conduct or liability that cannot legally be transferred to you." },
    ],
  },
  {
    n: 27,
    title: "Governing Law and Dispute Resolution",
    blocks: [
      { p: "These Terms are governed by the laws of the Republic of Türkiye, without regard to its conflict-of-law principles." },
      { p: "Any dispute arising out of or relating to these Terms shall first be submitted to arbitration in accordance with the applicable arbitration laws of the Republic of Türkiye. The seat of arbitration shall be Istanbul, Türkiye, and the arbitration shall be conducted in English, unless the parties agree otherwise." },
      { p: "If a dispute is not capable of being resolved by arbitration, or if arbitration is unavailable or legally ineffective, the courts and enforcement offices located on the European side of Istanbul, Türkiye, shall have exclusive jurisdiction, subject to any mandatory rights or procedures applicable to the parties." },
    ],
  },
  {
    n: 28,
    title: "Notices and Communications",
    blocks: [
      { p: "Speafy may communicate with you regarding the Program using the email address or other contact information associated with your partner account." },
      { p: "You are responsible for keeping your contact information accurate and checking communications relating to the Program." },
      { p: "Notices from you to Speafy should be sent through the contact method designated by Speafy for the Program." },
    ],
  },
  {
    n: 29,
    title: "Entire Agreement",
    blocks: [
      { p: "These Terms, together with any documents or policies expressly incorporated into them, constitute the agreement between you and Speafy concerning your participation in the Teacher Partner Program and supersede prior communications concerning the same subject matter." },
      { p: "If any provision is found to be invalid or unenforceable, the remaining provisions will remain effective to the extent permitted by law." },
      { p: "Failure by Speafy to enforce a provision immediately does not constitute a waiver of its right to enforce that provision later." },
    ],
  },
  {
    n: 30,
    title: "Electronic Acceptance",
    blocks: [
      { p: "These Terms may be accepted electronically." },
      { p: "By selecting the acceptance checkbox in your Speafy account and completing the applicable registration or acceptance process, you confirm that:" },
      { ul: [
        "you have read and understood these Teacher Partner Terms;",
        "you agree to be legally bound by these Terms;",
        "you agree to the commission, referral, payout, termination, and other provisions described above;",
        "you confirm that the information you provide to Speafy is accurate;",
        "you consent to Speafy maintaining a record of your acceptance electronically;",
        "you consent to Speafy recording, as part of that acceptance record, the date and time of acceptance, your account identifier and email address, the version of these Terms accepted, and technical information including your IP address and browser user agent; and",
        "you understand that your electronic acceptance has the same intended contractual effect as your written acceptance, to the extent permitted by applicable law.",
      ] },
      // The acceptance control itself (checkbox + Accept button in the app; "log in to accept"
      // button on the public page) is rendered per-surface, AFTER this heading.
      { h3: TEACHER_TERMS_ACCEPTANCE.requiredHeading },
    ],
  },
];

// Flat TOC — [{ n, id, title }] — both surfaces build their table of contents from this.
export const TEACHER_TERMS_TOC = TEACHER_TERMS_SECTIONS.map((s) => ({
  n: s.n,
  id: `section-${s.n}`,
  title: `${s.n}. ${s.title}`,
}));
